/**
 * AST 기반 자동 생성된 검증 함수
 * 이 파일은 scripts/generate-validators.ts에 의해 자동 생성됩니다.
 * 수동으로 수정하지 마세요.
 * 
 * 생성 시간: 2026-10-07T08:44:05.532Z
 * 소스 파일: src/data/dto/notice/notice-dto
 */

import type { NoticeDto } from "@/data/dto/notice/notice-dto";

/**
 * NoticeDto 타입 가드 함수
 * AST 분석을 통해 자동 생성된 최적화된 검증 함수
 */
export function isNoticeDto(input: unknown): input is NoticeDto {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  
  const obj = input as Record<string, unknown>;
  
  return (
    (obj.uuid === undefined || typeof obj.uuid === "string") &&
    typeof obj.updatedAt === "string" &&
    typeof obj.title === "string" &&
    (obj.body === undefined || (obj.body == null || typeof obj.body === "string")) &&
    typeof obj.type === "string" &&
    typeof obj.isPriority === "number" && !isNaN(obj.isPriority) &&
    typeof obj.priorityNumber === "number" && !isNaN(obj.priorityNumber) &&
    typeof obj.startViewDate === "string" &&
    typeof obj.endViewDate === "string" &&
    typeof obj.appVersion === "string" &&
    typeof obj.writerName === "string" && obj.writerName.trim() !== "" &&
    (obj.isActive === undefined || typeof obj.isActive === "boolean") &&
    (obj.isEditable === undefined || true) &&
    (obj.attachmentUuid === undefined || (obj.attachmentUuid == null || typeof obj.attachmentUuid === "string")) &&
    (obj.attachmentTitle === undefined || (obj.attachmentTitle == null || typeof obj.attachmentTitle === "string")) &&
    (obj.attachmentType === undefined || (obj.attachmentType == null || typeof obj.attachmentType === "string")) &&
    (obj.attachmentData === undefined || (obj.attachmentData == null || typeof obj.attachmentData === "string")) &&
    (obj.attachmentPath === undefined || (obj.attachmentPath == null || typeof obj.attachmentPath === "string")) &&
    (obj.attachments === undefined || true)
  );
}

/**
 * NoticeDto 상세 검증 결과 반환 함수
 * 검증 실패 시 상세한 에러 정보를 반환합니다.
 */
export interface IValidation<T> {
  success: boolean;
  data?: T;
  errors?: Array<{ path: string; expected: string; value: unknown }>;
}

export function validateNoticeDto(input: unknown): IValidation<NoticeDto> {
  const errors: Array<{ path: string; expected: string; value: unknown }> = [];
  
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push({
      path: "",
      expected: "object",
      value: input,
    });
    return {
      success: false,
      errors,
    };
  }
  
  const obj = input as Record<string, unknown>;
  
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      errors.push({ path: "", expected: "object", value: obj });
    } else {
      const objObj = obj as Record<string, unknown>;
      // uuid (optional)
      if (objObj.uuid !== undefined) {
        if (typeof objObj.uuid !== "string") {
          errors.push({ path: "uuid", expected: "string", value: objObj.uuid });
        }
      }
      // updatedAt (required)
      if (objObj.updatedAt === undefined) {
        errors.push({ path: "updatedAt", expected: "string", value: undefined });
      } else {
        if (typeof objObj.updatedAt !== "string") {
          errors.push({ path: "updatedAt", expected: "string", value: objObj.updatedAt });
        }
      }
      // title (required)
      if (objObj.title === undefined) {
        errors.push({ path: "title", expected: "string", value: undefined });
      } else {
        if (typeof objObj.title !== "string") {
          errors.push({ path: "title", expected: "string", value: objObj.title });
        }
      }
      // body (optional)
      if (objObj.body !== undefined) {
        if (!(objObj.body == null || typeof objObj.body === "string")) {
          errors.push({ path: "body", expected: "string | null", value: objObj.body });
        }
      }
      // type (required)
      if (objObj.type === undefined) {
        errors.push({ path: "type", expected: "string", value: undefined });
      } else {
        if (typeof objObj.type !== "string") {
          errors.push({ path: "type", expected: "string", value: objObj.type });
        }
      }
      // isPriority (required)
      if (objObj.isPriority === undefined) {
        errors.push({ path: "isPriority", expected: "number", value: undefined });
      } else {
        if (!(typeof objObj.isPriority === "number" && !isNaN(objObj.isPriority))) {
          errors.push({ path: "isPriority", expected: "number", value: objObj.isPriority });
        }
      }
      // priorityNumber (required)
      if (objObj.priorityNumber === undefined) {
        errors.push({ path: "priorityNumber", expected: "number", value: undefined });
      } else {
        if (!(typeof objObj.priorityNumber === "number" && !isNaN(objObj.priorityNumber))) {
          errors.push({ path: "priorityNumber", expected: "number", value: objObj.priorityNumber });
        }
      }
      // startViewDate (required)
      if (objObj.startViewDate === undefined) {
        errors.push({ path: "startViewDate", expected: "string", value: undefined });
      } else {
        if (typeof objObj.startViewDate !== "string") {
          errors.push({ path: "startViewDate", expected: "string", value: objObj.startViewDate });
        }
      }
      // endViewDate (required)
      if (objObj.endViewDate === undefined) {
        errors.push({ path: "endViewDate", expected: "string", value: undefined });
      } else {
        if (typeof objObj.endViewDate !== "string") {
          errors.push({ path: "endViewDate", expected: "string", value: objObj.endViewDate });
        }
      }
      // appVersion (required)
      if (objObj.appVersion === undefined) {
        errors.push({ path: "appVersion", expected: "string", value: undefined });
      } else {
        if (typeof objObj.appVersion !== "string") {
          errors.push({ path: "appVersion", expected: "string", value: objObj.appVersion });
        }
      }
      // writerName (required)
      if (objObj.writerName === undefined) {
        errors.push({ path: "writerName", expected: "string", value: undefined });
      } else {
        if (!(typeof objObj.writerName === "string" && objObj.writerName.trim() !== "")) {
          errors.push({ path: "writerName", expected: "string (non-empty)", value: objObj.writerName });
        }
      }
      // isActive (optional)
      if (objObj.isActive !== undefined) {
        if (typeof objObj.isActive !== "boolean") {
          errors.push({ path: "isActive", expected: "boolean", value: objObj.isActive });
        }
      }
      // isEditable (optional)
      if (objObj.isEditable !== undefined) {
      
      }
      // attachmentUuid (optional)
      if (objObj.attachmentUuid !== undefined) {
        if (!(objObj.attachmentUuid == null || typeof objObj.attachmentUuid === "string")) {
          errors.push({ path: "attachmentUuid", expected: "string | null", value: objObj.attachmentUuid });
        }
      }
      // attachmentTitle (optional)
      if (objObj.attachmentTitle !== undefined) {
        if (!(objObj.attachmentTitle == null || typeof objObj.attachmentTitle === "string")) {
          errors.push({ path: "attachmentTitle", expected: "string | null", value: objObj.attachmentTitle });
        }
      }
      // attachmentType (optional)
      if (objObj.attachmentType !== undefined) {
        if (!(objObj.attachmentType == null || typeof objObj.attachmentType === "string")) {
          errors.push({ path: "attachmentType", expected: "string | null", value: objObj.attachmentType });
        }
      }
      // attachmentData (optional)
      if (objObj.attachmentData !== undefined) {
        if (!(objObj.attachmentData == null || typeof objObj.attachmentData === "string")) {
          errors.push({ path: "attachmentData", expected: "string | null", value: objObj.attachmentData });
        }
      }
      // attachmentPath (optional)
      if (objObj.attachmentPath !== undefined) {
        if (!(objObj.attachmentPath == null || typeof objObj.attachmentPath === "string")) {
          errors.push({ path: "attachmentPath", expected: "string | null", value: objObj.attachmentPath });
        }
      }
      // attachments (optional)
      if (objObj.attachments !== undefined) {
      
      }
    }
  
  if (errors.length === 0 && isNoticeDto(input)) {
    return {
      success: true,
      data: input,
    };
  }
  
  return {
    success: false,
    errors: errors.length > 0 ? errors : [
      {
        path: "",
        expected: "NoticeDto",
        value: input,
      },
    ],
  };
}

/**
 * NoticeDto 검증 후 에러 throw 함수
 * 검증 실패 시 상세한 에러 정보를 포함하여 에러를 throw합니다.
 */
export function assertNoticeDto(input: unknown): NoticeDto {
  const validation = validateNoticeDto(input);
  if (!validation.success) {
    const errorDetails = validation.errors?.map(err => 
      `  - ${err.path}: 예상 타입 ${err.expected}, 실제 값: ${JSON.stringify(err.value)}`
    ).join("\n") || "알 수 없는 오류";
    throw new Error(`[assertNoticeDto] :: 검증 실패: NoticeDto 타입과 일치하지 않습니다\n\n오류 상세:\n${errorDetails}`);
  }
  return validation.data!;
}
